'use client'

import Image from 'next/image'
import { useState } from 'react'

type DemoVideo = {
  label: string
  title: string
  text: string
  src?: string
  poster?: string
  /** When set, the card shows these stacked screenshots as a static tile — no "View Demo" button or video modal. */
  images?: { src: string; width: number; height: number }[]
  /** Tailwind background class for the static screenshot tile. */
  thumbBg?: string
  /** Extra Tailwind classes applied to each screenshot image (e.g. a max-width cap). */
  thumbImgClassName?: string
}

type DemoVideoGridProps = {
  videos: DemoVideo[]
}

export function DemoVideoGrid({ videos }: DemoVideoGridProps) {
  const [activeVideo, setActiveVideo] = useState<DemoVideo | null>(null)

  return (
    <>
      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        {videos.map((video) => (
          <article
            key={video.title}
            className="overflow-hidden rounded-3xl border border-[#E8E2E9] bg-white shadow-[0_12px_32px_rgba(43,10,74,0.07)] transition-transform hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(43,10,74,0.12)]"
          >
            {video.images && video.images.length > 0 ? (
              <div className={`flex aspect-video flex-col items-center justify-center gap-2.5 p-4 ${video.thumbBg ?? 'bg-[#F3E3FB]'}`}>
                {video.images.map((img) => (
                  <span key={img.src} className="flex min-h-0 w-full flex-1 items-center justify-center">
                    <Image
                      src={img.src}
                      alt={`${video.title} screenshot`}
                      width={img.width}
                      height={img.height}
                      sizes="(min-width: 1024px) 50vw, 100vw"
                      className={`max-h-full w-auto max-w-full rounded-lg border border-black/[0.06] bg-white shadow-[0_6px_16px_rgba(43,10,74,0.10)] ${video.thumbImgClassName ?? ''}`}
                    />
                  </span>
                ))}
              </div>
            ) : (
              <div className="bg-[#120527] p-3">
                <div className="relative overflow-hidden rounded-2xl bg-[#120527]">
                  <Image
                    src={video.poster ?? ''}
                    alt={`${video.title} thumbnail`}
                    width={960}
                    height={540}
                    sizes="(min-width: 1024px) 50vw, 100vw"
                    className="aspect-video w-full object-cover"
                  />
                  {video.src ? (
                    <button
                      type="button"
                      onClick={() => setActiveVideo(video)}
                      className="absolute inset-0 flex items-center justify-center bg-black/18 text-sm font-bold text-white transition hover:bg-black/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-6px] focus-visible:outline-white"
                    >
                      <span className="rounded-full bg-[#FFC933] px-5 py-2 text-[#1B0044] shadow-lg">View Demo</span>
                    </button>
                  ) : null}
                </div>
              </div>
            )}
            <div className="px-7 pb-8 pt-6">
              <p className="mb-3 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#376F4E]">{video.label}</p>
              <h3 className="text-2xl font-bold leading-tight tracking-[-0.035em] text-[#21302B]">{video.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#46534F]">{video.text}</p>
            </div>
          </article>
        ))}
      </div>

      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#120527]/85 px-4 py-8 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-video-title"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 border-b border-[#E8E2E9] px-5 py-4">
              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#376F4E]">{activeVideo.label}</p>
                <h3 id="demo-video-title" className="mt-1 text-xl font-bold text-[#1B0044]">
                  {activeVideo.title}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setActiveVideo(null)}
                className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-[#F5EEF8] text-lg font-bold text-[#1B0044] transition hover:bg-[#E8DDF0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1B0044]"
                aria-label="Close demo video"
              >
                X
              </button>
            </div>
            <div className="bg-[#120527] p-2">
              <video className="max-h-[72vh] w-full rounded-2xl bg-[#120527]" controls autoPlay playsInline>
                <source src={activeVideo.src} type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      )}
    </>
  )
}